import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-artsy-background">
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-white border-b-2 border-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Get in <span className="text-pink-500">Touch</span>
          </h1>
          <p className="font-body text-lg text-slate-600">
            Have questions about Artsy Craftsy? We'd love to hear from you!
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="card-pricing">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-2">Your Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-artsy"
                    required
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-artsy"
                    required
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-2">Subject</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="input-artsy"
                    required
                    data-testid="contact-subject"
                  />
                </div>
                <div>
                  <label className="block font-body font-semibold text-slate-700 mb-2">Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="input-artsy min-h-[150px] resize-none"
                    required
                    data-testid="contact-message"
                  />
                </div>
                <button 
                  type="submit" 
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  disabled={loading}
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="card-sticker">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center text-white border-2 border-slate-900 shadow-sticker flex-shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-slate-900 mb-1">Email Us</h3>
                    <p className="font-body text-slate-600">hello@artsycrafsy.art</p>
                    <p className="font-body text-sm text-slate-500 mt-1">We usually respond within 24 hours</p>
                  </div>
                </div>
              </div>

              <div className="card-sticker">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-pink-500 rounded-xl flex items-center justify-center text-white border-2 border-slate-900 shadow-sticker flex-shrink-0">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-slate-900 mb-1">Call Us</h3>
                    <p className="font-body text-slate-600">07123 456789</p>
                    <p className="font-body text-sm text-slate-500 mt-1">Mon-Fri, 9am-5pm</p>
                  </div>
                </div>
              </div>

              <div className="card-sticker">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center text-white border-2 border-slate-900 shadow-sticker flex-shrink-0">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-slate-900 mb-1">Location</h3>
                    <p className="font-body text-slate-600">Primary Schools across London</p>
                    <p className="font-body text-sm text-slate-500 mt-1">Classes held at various school locations</p>
                  </div>
                </div>
              </div>

              {/* FAQ Teaser */}
              <div className="card-sticker border-teal-600">
                <h3 className="font-heading text-xl font-bold text-teal-600 mb-4">Common Questions</h3>
                <div className="space-y-4 font-body text-slate-700">
                  <div>
                    <p className="font-semibold">What ages do you teach?</p>
                    <p className="text-slate-600">Years 1-6 (ages 5-11)</p>
                  </div>
                  <div>
                    <p className="font-semibold">What do I need to bring?</p>
                    <p className="text-slate-600">Just your child! All materials are provided.</p>
                  </div>
                  <div>
                    <p className="font-semibold">How do I pay?</p>
                    <p className="text-slate-600">Secure online payment via Stripe.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
